import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Skeleton,
  TableRow,
  TableCell,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { Search as SearchIcon } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import { createAnalysisTableColumns } from './AnalysisTableColumns'
import { useFileActions } from '../FileUploadView/hooks/useFileActions'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import { useSetAnalysisVisibility } from '../../api/analysis/visibility'
import { useAnalysis } from 'src/api/analysis/list'
import { foldPairedRnaRows } from './lib/pairedRna'
import usePairedRnaFallback from './lib/usePairedRnaFallback'
import useAuth from '../../hooks/useAuth'
import { useParams, useLocation, useNavigate } from 'react-router-dom' // Import useLocation and useNavigate

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  tableContainer: {
    position: 'relative',
    minHeight: '200px',
  },
  // One row: title on the left, search + chips pushed to the right. As the
  // card narrows, the search gives up room first (320px down to 264px), then
  // the chip strip scrolls sideways inside itself -- it never wraps onto a
  // second row or widens the card.
  filtersContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: 0,
    padding: theme.spacing(2),
    minWidth: 0,
    // Phones cannot fit the title beside the controls: title, search and the
    // (still scrolling) chip strip each get a full-width line there.
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
  },
  title: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: '0 1 auto',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
  },
  searchField: {
    // A real width (not only a flex-basis) so the row's intrinsic size counts
    // the whole field and the chip strip is not squeezed while there is room.
    // The large shrink weight makes the search narrow before the chips scroll.
    width: 320,
    flexShrink: 10000,
    minWidth: 264,
    '& .MuiInputBase-input': {
      fontSize: 14,
      textOverflow: 'ellipsis',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minWidth: 0,
    },
  },
  filterChips: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 6,
    flex: '0 1 auto',
    minWidth: 96,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& .MuiChip-root': {
      flexShrink: 0,
      height: 24,
    },
    // Selected (filled) chips get the same 1px border as outlined ones, only
    // transparent, so toggling a chip never changes its width and the
    // right-aligned row does not shift by 2px on every click.
    '& .MuiChip-root:not(.MuiChip-outlined)': {
      border: '1px solid transparent',
    },
    '& .MuiChip-label': {
      fontSize: 12.5,
      paddingLeft: 5,
      paddingRight: 5,
    },
    // Keep a little air between the Hidden chip's eye icon and its label.
    '& .MuiChip-root .MuiChip-iconSmall': {
      marginLeft: 4,
      marginRight: -2,
    },
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
      minWidth: 0,
    },
  },
  blurry: {
    filter: 'blur(3px)',
    pointerEvents: 'none',
    transition: 'filter 0.2s',
    position: 'relative',
    zIndex: 1,
  },
}))

const AnalysisTable = React.memo(({ organizationId }) => {
  const classes = useStyles()
  const location = useLocation() // Initialize useLocation hook
  const navigate = useNavigate() // Initialize useNavigate hook

  // Parse query parameters from the URL
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const pageFromUrl = parseInt(queryParams.get('page')) || 0 // Default to 0 if not found
  const rowsPerPageFromUrl = parseInt(queryParams.get('rowsPerPage')) || 10 // Default to 10 if not found

  // Pagination and filtering state
  const [filters, setFilters] = useState({
    tags: ['All'],
  })
  // Hidden is a view MODE rather than another tag: an operator browses either
  // the live list or the hidden one, so it never combines with tag filters.
  const [showHidden, setShowHidden] = useState(false)
  const [sortOrder, setSortOrder] = useState('desc')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [page, setPage] = useState(pageFromUrl)
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageFromUrl)

  // Sync state with URL query parameters on initial load or URL change
  useEffect(() => {
    setPage(pageFromUrl)
    setRowsPerPage(rowsPerPageFromUrl)
  }, [pageFromUrl, rowsPerPageFromUrl])

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Chip strip overflow: fade whichever edge has more chips beyond it, and let
  // a plain mouse wheel scroll the strip sideways (its scrollbar is hidden).
  const chipStripRef = useRef(null)
  const [chipFade, setChipFade] = useState({ left: false, right: false })
  const updateChipFade = useCallback(() => {
    const el = chipStripRef.current
    if (!el) return
    const left = el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setChipFade((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])
  useEffect(() => {
    const el = chipStripRef.current
    if (!el) return undefined
    updateChipFade()
    const onWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 0) return
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY // lines -> px
      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + delta))
      if (next === el.scrollLeft) return // at an end: let the page scroll
      event.preventDefault()
      el.scrollLeft = next
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateChipFade) : null
    if (resizeObserver) resizeObserver.observe(el)
    else window.addEventListener('resize', updateChipFade)
    // Chip widths change once the web font lands, without the strip resizing.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(updateChipFade)
    return () => {
      el.removeEventListener('wheel', onWheel)
      if (resizeObserver) resizeObserver.disconnect()
      else window.removeEventListener('resize', updateChipFade)
    }
  }, [updateChipFade])
  const chipStripMask =
    chipFade.left || chipFade.right
      ? `linear-gradient(to right, ${chipFade.left ? 'transparent 0, #000 24px' : '#000 0'}, ${
          chipFade.right ? '#000 calc(100% - 32px), transparent 100%' : '#000 100%'
        })`
      : undefined

  // Available tags
  const availableTags = [
    'Carrier',
    'WGS',
    'CES',
    'WES',
    'VCF',
    'CNV',
    'SV',
    'ONT',
    'PacBio',
    'AI',
    'Germline',
    'Somatic',
    'RNA',
    'Multisample',
  ]

  // Memoize the query parameters to prevent unnecessary re-renders for the API call
  const apiQueryParams = useMemo(
    () => ({
      page: page,
      limit: rowsPerPage,
      tags: filters.tags.includes('All') ? [] : filters.tags,
      sort_order: sortOrder,
      // Only include sample_name if search term is at least 3 characters
      ...(debouncedSearchTerm.length >= 3 && { sample_name: debouncedSearchTerm }),
      ...(organizationId && { organization_id: organizationId }),
      hidden: showHidden,
    }),
    [page, rowsPerPage, filters.tags, sortOrder, debouncedSearchTerm, organizationId, showHidden],
  )

  // Fetch data using the hook with memoized params
  const { query, refresh } = useAnalysis(apiQueryParams)
  const { data: analysisData, isLoading, isError, isPreviousData, isFetching } = query

  const { user, logout } = useAuth()

  // --- IMPORTANT: Transform the nested data into a flat list for the table ---
  const pageRows = useMemo(() => {
    if (!analysisData) return []
    // Backend returns data in 'results' field
    return analysisData.results.flat()
  }, [analysisData])
  // A paired RNA row is folded into its DNA row when both are on this page
  // (lib/pairedRna.js); the pairing is looked up while the payload lacks it.
  const pairedRows = usePairedRnaFallback(pageRows)
  const processedData = useMemo(() => foldPairedRnaRows(pairedRows), [pairedRows])

  const isLoadingState = isLoading || isFetching

  // Get total count for pagination
  const totalCount = useMemo(() => {
    if (!analysisData) return 0
    // Backend returns total count in pagination.total_count
    return analysisData.pagination?.total_count || 0
  }, [analysisData])

  const updateUrlParams = useCallback(
    (newParams) => {
      const currentParams = new URLSearchParams(location.search)
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          currentParams.set(key, value)
        } else {
          currentParams.delete(key)
        }
      })
      navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true })
    },
    [location.pathname, location.search, navigate],
  )
  // Clamp page to valid range when count or rows per page changes
  useEffect(() => {
    // Avoid clamping while loading to prevent flicker between requests
    if (isLoadingState) return

    const maxPage = totalCount > 0 ? Math.floor((totalCount - 1) / rowsPerPage) : 0
    if (page > maxPage) {
      setPage(maxPage)
      updateUrlParams({ page: maxPage })
    }
  }, [totalCount, rowsPerPage, page, isLoadingState, updateUrlParams])

  // Helper function to update URL query parameters

  // Handle page change
  const handleChangePage = useCallback(
    (currentPage) => {
      setPage(currentPage)
      updateUrlParams({ page: currentPage })
    },
    [updateUrlParams],
  )

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback(
    (numberOfRows) => {
      setRowsPerPage(numberOfRows)
      setPage(0) // Reset to first page when rows per page changes
      updateUrlParams({ rowsPerPage: numberOfRows, page: 0 }) // Also update page to 0 in URL
    },
    [updateUrlParams],
  )

  // Handle filter chip click
  const handleFilterChipClick = useCallback(
    (tag) => {
      setFilters((prevFilters) => {
        const currentTags = prevFilters.tags

        if (tag === 'All') {
          return { ...prevFilters, tags: ['All'] }
        }

        const isSelected = currentTags.includes(tag)

        let newTags
        if (isSelected) {
          newTags = currentTags.filter((t) => t !== tag)
          if (newTags.length === 0) newTags = ['All']
        } else {
          const tagsWithoutAll = currentTags.filter((t) => t !== 'All')
          newTags = [...tagsWithoutAll, tag]

          // If all specific tags are selected, automatically switch to All
          const specificTagsCount = availableTags.filter((t) => t !== 'All').length
          if (
            newTags.length === specificTagsCount &&
            availableTags.filter((t) => t !== 'All').every((t) => newTags.includes(t))
          ) {
            newTags = ['All']
          }
        }
        return { ...prevFilters, tags: newTags }
      })
      setPage(0) // Reset to first page when filters change
      updateUrlParams({ page: 0 }) // Update URL as well
    },
    [availableTags, updateUrlParams],
  )

  // Handle search change
  const handleSearchChange = useCallback(
    (event) => {
      const value = event.target.value
      setSearchTerm(value)
      setPage(0) // Reset to first page when search changes
      updateUrlParams({ page: 0 }) // Update URL as well
      // You might also want to update a 'search' query param here if desired
      // updateUrlParams('search', value.length >= 3 ? value : '');
    },
    [updateUrlParams],
  )

  // Handle sort change
  const handleSortChange = useCallback(
    (changedColumn, direction) => {
      console.log('Sort changed:', changedColumn, direction)
      // Only allow sorting on date field
      if (changedColumn === 'created_at') {
        setSortOrder(direction)
        setPage(0) // Reset to first page when sorting changes
        updateUrlParams({ sortOrder: direction, page: 0 }) // Update URL as well
      }
    },
    [updateUrlParams],
  )

  // --- Mock Action Handlers (replace with real logic from useFileActions if needed) ---
  const handleDownloadReport = useCallback((vcfId) => {
    console.log('Downloading report for VCF ID:', vcfId)
    // Example: actions.handleDownloadReport(vcfId)
  }, [])

  const handleDownloadVcf = useCallback((vcfId) => {
    console.log('Downloading VCF for ID:', vcfId)
    // Example: actions.handleDownloadRawVcf(vcfId)
  }, [])

  // Hide / restore. Replaces a delete stub that only ever console.logged —
  // deleting an AnalysisWrapper is not offered because it is not a soft-delete
  // model and would cascade to the customer's FASTQ pair, CNV analysis and VCFs.
  const setVisibility = useSetAnalysisVisibility()
  const handleToggleHidden = useCallback(
    (analysisId, nextHidden) => {
      if (
        nextHidden &&
        !window.confirm(
          'Hide this analysis from the list?\n\n' +
            'It stays in the database and everyone in your organisation can restore it ' +
            'from the Hidden filter.',
        )
      ) {
        return
      }
      setVisibility.mutate({ analysisId, isHidden: nextHidden })
    },
    [setVisibility],
  )

  // --- Create columns using the new function ---
  const showBlurryLoading = isLoadingState && isPreviousData
  const isInitialLoading = isLoadingState && !isPreviousData

  const columns = useMemo(
    () =>
      createAnalysisTableColumns({
        user,
        processedData,
        analysisData: processedData,
        handleDownloadReport,
        handleDownloadVcf,
        handleToggleHidden,
        showBlurryLoading,
        onRefresh: refresh,
      }),
    [processedData, handleDownloadReport, handleDownloadVcf, handleToggleHidden, showBlurryLoading, user, refresh],
  )

  return (
    <Box>
      {/* Table */}
      <Box className={classes.wrapper}>
        {/* Title, Search, and Filter Chips on same line */}
        <Box className={classes.filtersContainer}>
          <Typography variant="h6" className={classes.title}>
            Analysis History
          </Typography>
          <Box className={classes.searchContainer}>
            <TextField
              className={classes.searchField}
              placeholder="Search by sample name (min 3 chars)..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              ref={chipStripRef}
              className={classes.filterChips}
              onScroll={updateChipFade}
              style={chipStripMask ? { WebkitMaskImage: chipStripMask, maskImage: chipStripMask } : undefined}
            >
              {/* Select All chip */}
              <Chip
                key="All"
                label="All"
                color={filters.tags.includes('All') ? 'primary' : 'default'}
                variant={filters.tags.includes('All') ? 'default' : 'outlined'}
                onClick={() => handleFilterChipClick('All')}
                clickable
                size="small"
              />
              {availableTags
                .filter((tag) => tag !== 'All')
                .map((tag) => {
                  const isSelected = filters.tags.includes(tag)
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'default' : 'outlined'}
                      onClick={() => handleFilterChipClick(tag)}
                      clickable
                      size="small"
                    />
                  )
                })}
              {/* Hidden is a MODE, set apart from the tag chips by its icon and
                  its own colour so it does not read as one more tag to combine. */}
              <Chip
                key="Hidden"
                icon={<VisibilityOffIcon style={{ fontSize: 16 }} />}
                label="Hidden"
                color={showHidden ? 'secondary' : 'default'}
                variant={showHidden ? 'default' : 'outlined'}
                onClick={() => {
                  setShowHidden((v) => !v)
                  setPage(0)
                }}
                clickable
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.tableContainer}>
          <MUIDataTable
            title=""
            data={processedData}
            columns={columns}
            options={{
              filterType: 'multiselect',
              responsive: 'standard',
              elevation: 0,
              selectableRows: 'none',
              serverSide: true,
              count: totalCount,
              page: page,
              rowsPerPage: rowsPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
              search: false,
              sort: true,
              filter: false,
              print: false,
              download: false,
              viewColumns: false,
              onColumnSortChange: handleSortChange,
              loading: isLoadingState,
              textLabels: {
                body: {
                  noMatch: isInitialLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress />
                    </div>
                  ) : (
                    'Sorry, no matching records found'
                  ),
                  toolTip: 'Sort',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  )
})

AnalysisTable.displayName = 'AnalysisTable'

AnalysisTable.propTypes = {
  organizationId: PropTypes.string,
}

export default AnalysisTable
